import { useState, useEffect, useRef } from "react";
import { Search20Regular } from "@fluentui/react-icons";
import { useRootStore } from "store";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import "./SearchTab.scss";

export default function SearchTab() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { isReplying, isDirty, setIsReplying, clearDirty } = useInboxReplyContext();
  const shouldShowModal = isReplying && isDirty;

  const workingCustomerId = useRootStore((s) => s.global.workingCustomerId);
  const {
    fetchMyThreads,
    fetchAllThreads,
    setSentMessagesSearchTerm,
    sentMessagesSearchTerm
  } = useRootStore((state) => state.inbox);

  const handleModalConfirm = () => {
    setIsReplying(false);
    clearDirty();
    performSearch(pendingSearchTerm);
    setShowModal(false);
    setPendingSearchTerm("");
  };

  const handleModalCancel = () => {
    setSearchTerm("");
    setShowModal(false);
    setPendingSearchTerm("");
  };

  const performSearch = (term: string) => {
    fetchMyThreads(workingCustomerId, 0, term);
    fetchAllThreads(workingCustomerId, 0, term);
    setSentMessagesSearchTerm(term);
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);

    // If there's a search term, clear it and reset search
    if (searchTerm) {
      setSearchTerm("");
      setSentMessagesSearchTerm("");
      fetchMyThreads(workingCustomerId, 0, "");
      fetchAllThreads(workingCustomerId, 0, "");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Debounced search effect
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // don't send requests if search term is unchanged
    if (sentMessagesSearchTerm === searchTerm) {
      return;
    }

    // Only search when we have a working customer ID
    if (workingCustomerId) {
      // Set new timer for debounced search
      debounceTimerRef.current = setTimeout(() => {
        if (shouldShowModal) {
          setPendingSearchTerm(searchTerm);
          setShowModal(true);
        } else {
          performSearch(searchTerm);
        }
      }, 300); // 300ms debounce delay
    }

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, workingCustomerId]);

  return (
    <div
      className={`search-tab-container ${isExpanded ? "expanded" : "collapsed"}`}
      onClick={!isExpanded ? handleExpand : undefined}
      role={!isExpanded ? "button" : undefined}
      aria-label={!isExpanded ? "Expand search" : undefined}
      tabIndex={!isExpanded ? 0 : undefined}
      onKeyDown={!isExpanded ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleExpand();
        }
      } : undefined}
    >
      <div
        className="search-tab-button"
        aria-label="Search"
        aria-expanded={isExpanded}
      >
        <div className="search-tab-content">
          <div
            className="search-icon-wrapper"
            onClick={isExpanded ? handleCollapse : undefined}
            role={isExpanded ? "button" : undefined}
            aria-label={isExpanded ? "Collapse search" : undefined}
            tabIndex={isExpanded ? 0 : -1}
            onKeyDown={isExpanded ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCollapse();
              }
            } : undefined}
          >
            <Search20Regular />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Sök"
            aria-label="Search input"
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus={isExpanded}
            disabled={!isExpanded}
            tabIndex={isExpanded ? 0 : -1}
          />
        </div>
      </div>
      
      {showModal && (
        <UnsavedChangesModal
          text="Du har ändringar som ej sparats i ditt svar. Är du säker på att du vill söka och lämna utan att spara?"
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          onDismiss={handleModalCancel}
        />
      )}
    </div>
  );
}
